import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["Component"];
import React, { useEffect } from 'react';
import { di } from 'react-magnetic-di';
import { ErrorMessage } from '@atlaskit/form';
import { JQLSyntaxError } from '@atlaskit/jql-ast';
import { fg } from '@atlaskit/platform-feature-flags';
import { JQL_EDITOR_INPUT_ID, JQL_EDITOR_VALIDATION_ID } from '../../../../common/constants';
import { commonMessages } from '../../../../common/messages';
import { useEditorThemeContext } from '../../../../hooks/use-editor-theme';
import { useEditorViewIsInvalid } from '../../../../hooks/use-editor-view-is-invalid';
import { useCustomErrorComponent, useExternalMessages, useIntl, useJqlError, useScopedId, useStoreActions } from '../../../../state';
import { FormatMessages, MessageContainer } from '../format';
import { messages } from './messages';
var useFormattedErrorMessage = function useFormattedErrorMessage() {
  var _useIntl = useIntl(),
    _useIntl2 = _slicedToArray(_useIntl, 1),
    formatMessage = _useIntl2[0].formatMessage;
  var _useJqlError = useJqlError(),
    _useJqlError2 = _slicedToArray(_useJqlError, 1),
    jqlError = _useJqlError2[0];
  var _useExternalMessages = useExternalMessages(),
    _useExternalMessages2 = _slicedToArray(_useExternalMessages, 1),
    externalErrors = _useExternalMessages2[0].errors;
  var _useStoreActions = useStoreActions(),
    _useStoreActions2 = _slicedToArray(_useStoreActions, 2),
    externalErrorMessageViewed = _useStoreActions2[1].externalErrorMessageViewed;
  var editorViewIsInvalid = useEditorViewIsInvalid();
  useEffect(function () {
    // Emit a UI event whenever external errors has changed
    externalErrorMessageViewed();
  }, [externalErrorMessageViewed, externalErrors]);
  if (!editorViewIsInvalid) {
    return null;
  }

  // Prioritise client over external errors
  if (jqlError instanceof JQLSyntaxError) {
    return "".concat(jqlError.message, " ").concat(formatMessage(messages.jqlErrorPosition, {
      lineNumber: jqlError.line,
      charPosition: jqlError.charPositionInLine + 1
    }));
  } else if (externalErrors.length > 0) {
    return /*#__PURE__*/React.createElement(FormatMessages, {
      messages: externalErrors
    });
  } else if (jqlError !== null) {
    return formatMessage(commonMessages.unknownError);
  }
  return null;
};
/**
 * This is a decorator component to include the editorTheme prop to the already passed list of props
 */
var CustomComponentDecoratedWithEditorTheme = function CustomComponentDecoratedWithEditorTheme(props) {
  var Component = props.Component,
    rest = _objectWithoutProperties(props, _excluded);
  var editorThemeContext = useEditorThemeContext();

  // This is a slightly redundant condition.
  // Technically, CustomErrorMessage component should never be called with an undefined Component
  // This is enforced by the static types. Adding this condition as an extra layer of protection
  if (!Component) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, "props.children");
  }
  return /*#__PURE__*/React.createElement(Component, _extends({
    editorTheme: editorThemeContext
  }, rest));
};
export var ErrorMessages = function ErrorMessages() {
  var _useScopedId = useScopedId(JQL_EDITOR_INPUT_ID),
    _useScopedId2 = _slicedToArray(_useScopedId, 1),
    editorId = _useScopedId2[0];
  var _useScopedId3 = useScopedId(JQL_EDITOR_VALIDATION_ID),
    _useScopedId4 = _slicedToArray(_useScopedId3, 1),
    validationId = _useScopedId4[0];
  var errorMessage = useFormattedErrorMessage();
  var _useCustomErrorCompon = useCustomErrorComponent(),
    _useCustomErrorCompon2 = _slicedToArray(_useCustomErrorCompon, 1),
    CustomErrorComponent = _useCustomErrorCompon2[0];
  var childrenToRender = errorMessage != null ? /*#__PURE__*/React.createElement(MessageContainer, null, /*#__PURE__*/React.createElement(ErrorMessage, {
    testId: JQL_EDITOR_VALIDATION_ID
  }, fg('jql_editor_a11y') ? /*#__PURE__*/React.createElement("span", {
    role: "alert",
    id: validationId
  }, errorMessage) : /*#__PURE__*/React.createElement("span", {
    role: "alert",
    "aria-describedby": editorId
  }, errorMessage))) : null;

  // Only render CustomErrorComponent if there is an errorMessage
  if (errorMessage != null && CustomErrorComponent) {
    return /*#__PURE__*/React.createElement(CustomComponentDecoratedWithEditorTheme, {
      testId: JQL_EDITOR_VALIDATION_ID,
      editorId: editorId,
      Component: CustomErrorComponent
    }, childrenToRender);
  }
  return childrenToRender;
};